import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Gift, Share2, Users, DollarSign, Check } from "lucide-react";
import { shopApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ReferralCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["myReferral"], queryFn: shopApi.myReferral });
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  if (isLoading || !data) return null;
  const shareLink = `${window.location.origin}/signup?ref=${data.referral_code}`;
  const copy = async () => { await navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const apply = async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const r = await shopApi.applyReferral(applyCode.trim());
      toast.success(`Coupon ${r.coupon_code} unlocked — ${r.percent_off}% off your first order`);
      qc.invalidateQueries({ queryKey: ["myReferral"] });
      setApplyCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    setApplying(false);
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: "NovalynxTech", text: `Get 10% off your first order at NovalynxTech with my code ${data.referral_code}`, url: shareLink }).catch(() => {});
    } else { copy(); }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-primary/15 via-card to-card border border-primary/20 p-6" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-1">
        <Gift className="h-5 w-5 text-primary" />
        <h2 className="font-display text-xl font-bold">Refer a friend, both win</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Share your code — friends get <strong className="text-white">10% off</strong> their first order, and you earn <strong className="text-primary">$5 store credit</strong> after they check out.</p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat icon={Users} label="Friends invited" value={data.invited_count} />
        <Stat icon={Check} label="Rewarded orders" value={data.rewarded_count} />
        <Stat icon={DollarSign} label="Store credit" value={`$${(data.store_credit || 0).toFixed(2)}`} highlight />
      </div>

      <div className="rounded-xl bg-black/40 border border-white/10 p-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Your referral code</p>
        <div className="flex items-center gap-2">
          <code className="text-2xl font-display font-bold text-primary" data-testid="referral-code">{data.referral_code}</code>
          <Button variant="outline" size="sm" className="ml-auto border-white/15" onClick={copy} data-testid="copy-referral-link">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button size="sm" className="btn-primary" onClick={share} data-testid="share-referral">
            <Share2 className="h-4 w-4 mr-1" /> Share
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 break-all">{shareLink}</p>
      </div>

      {!data.referred_by && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Got a friend's code?</p>
          <div className="flex gap-2">
            <Input value={applyCode} onChange={(e) => setApplyCode(e.target.value.toUpperCase())} placeholder="NOVA123ABC" data-testid="apply-referral-input" />
            <Button className="btn-primary" onClick={apply} disabled={applying || !applyCode.trim()} data-testid="apply-referral-btn">{applying ? "…" : "Apply"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, highlight }) => (
  <div className={`rounded-xl p-3 border ${highlight ? "border-primary/40 bg-primary/10" : "border-white/5 bg-black/20"}`}>
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground mb-1"><Icon className="h-3 w-3" /> {label}</div>
    <p className={`font-display font-bold ${highlight ? "text-primary text-xl" : "text-lg"}`}>{value}</p>
  </div>
);
